import { useEffect, useMemo, useState } from 'react';
import {
    Home,
    BriefcaseBusiness,
    ChartColumn,
    CalendarDays,
    FolderOpen,
    ScrollText,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, type MenuProps } from 'antd';
import { useAuth } from '@hooks/useAuth';
import { usePermission } from '@hooks/usePermission';
import { PERM } from '@constants/permissions';

interface SidebarTeacherProps {
    isFullSize?: boolean;
}

const SUB_REPORT = 'sub:report';

function resolveSelectedMenuKey(path: string): string {
    const ordered = [
        '/teacher/report/exams',
        '/teacher/report/practice',
        '/teacher/distribution-evaluation',
        '/teacher/calendar',
        '/teacher/modules',
        '/teacher/class',
        '/teacher/dashboard',
    ];
    for (const route of ordered) {
        if (route === '/teacher/dashboard') {
            if (path === '/teacher/dashboard') return route;
        } else if (path === route || path.startsWith(`${route}/`)) {
            return route;
        }
    }
    return '';
}

const SidebarTeacher = ({ isFullSize = false }: SidebarTeacherProps) => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const currentPath = location.pathname;
    const { auth, isTeacher } = useAuth();
    const isK12Teacher = isTeacher && Boolean(auth?.k12_username);
    const { can } = usePermission();

    const [openKeys, setOpenKeys] = useState<string[]>([]);

    useEffect(() => {
        if (currentPath.startsWith('/teacher/report')) {
            setOpenKeys((prev) => (prev.includes(SUB_REPORT) ? prev : [...prev, SUB_REPORT]));
        } else {
            setOpenKeys((prev) => prev.filter((k) => k !== SUB_REPORT));
        }
    }, [currentPath]);

    const selectedKey = useMemo(() => resolveSelectedMenuKey(currentPath), [currentPath]);

    // Ẩn tab không có quyền (route vẫn chặn bằng PermissionGuard)
    const items: MenuProps['items'] = useMemo(
        () => [
            can(PERM.teacher.dashboard) && {
                key: '/teacher/dashboard',
                icon: <Home size={23} className="shrink-0" />,
                label: t('rolemenu.teacher.home'),
            },
            can(PERM.teacher.class) && {
                key: '/teacher/class',
                icon: <BriefcaseBusiness size={23} className="shrink-0" />,
                label: t('rolemenu.teacher.class'),
            },
            // Ẩn Lịch học với giáo viên K12
            ...(!isK12Teacher && can(PERM.teacher.calendar) ? [{
                key: '/teacher/calendar',
                icon: <CalendarDays size={23} className="shrink-0" />,
                label: t('rolemenu.teacher.calendar'),
            }] : []),
            can(PERM.teacher.distribution) && {
                key: '/teacher/distribution-evaluation',
                icon: <ScrollText size={23} className="shrink-0" />,
                label: t('rolemenu.teacher.distribute-material'),
            },
            can(PERM.teacher.modules) && {
                key: '/teacher/modules',
                icon: <FolderOpen size={23} className="shrink-0" />,
                label: 'Tài liệu giảng dạy',
            },
            can([PERM.teacher.reportPractice, PERM.teacher.reportExam]) && {
                key: SUB_REPORT,
                icon: <ChartColumn size={23} className="shrink-0" />,
                label: 'Báo cáo',
                children: [
                    can(PERM.teacher.reportPractice) && { key: '/teacher/report/practice', label: 'Báo cáo luyện tập' },
                    can(PERM.teacher.reportExam) && { key: '/teacher/report/exams', label: 'Báo cáo kiểm tra' },
                ].filter(Boolean) as MenuProps['items'],
            },
        ].filter(Boolean) as MenuProps['items'],
        [t, i18n.language, isK12Teacher, can],
    );

    const onMenuClick: MenuProps['onClick'] = (info) => {
        const { key } = info;
        if (typeof key === 'string' && key.startsWith('/')) {
            navigate(key);
        }
    };

    return (
        <div
            className={`${isFullSize ? 'w-[80px] px-2 border-r' : 'w-64 py-4 px-1 '} bg-white pb-0 text-gray3 font-bold text-[15px] flex flex-col relative h-full transition-all duration-300`}
        >
            <nav className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] py-4 pr-[6px]">
                <Menu
                    className="teacher-sidebar-menu !m-0 !min-h-0 !w-full !border-0 !bg-transparent pr-0"
                    mode="inline"
                    inlineCollapsed={isFullSize}
                    items={items}
                    selectedKeys={selectedKey ? [selectedKey] : []}
                    openKeys={openKeys}
                    onOpenChange={setOpenKeys}
                    onClick={onMenuClick}
                />
            </nav>
        </div>
    );
};

export default SidebarTeacher;
